/* eslint-disable no-param-reassign */
/*
 * Modelo 3D do transformador construído EM CÓDIGO (como transformadorProcedural.js),
 * mas com o LAYOUT DE PEÇAS do Transformador 1 (a malha de CAD carregada por
 * transformadorCad.js) — implementação da spec
 * cma-transformador1-para-procedural-potencia3.md.
 *
 * Objetivo: mesma estrutura de peças e mesmas direções de explosão/âncoras já
 * calibradas no CAD (`PART_EXPLODE_CAD`/`ANCHORS_CAD` de transformadorCad.js),
 * só que desenhadas com primitivas Three.js em vez de carregar um `.glb` —
 * assim o slot `potencia-3` ganha `parts` reais (explosão, foco por card,
 * highlight) sem os 189,5 MB de download.
 *
 * As cotas abaixo vêm da MEDIÇÃO do `.glb` de origem (bounding box de cada nó
 * nomeado, escalada pelo mesmo fator `alturaAlvo / altura(Body)` que a cena
 * já aplica ao CAD — ver TFMP_MESH em transformer3dModels.js) — não são uma
 * cópia exata (primitivas não reproduzem flange curva nem calota real), mas
 * mantêm a MESMA proporção geral e posição relativa das peças. Peças de
 * detalhe sem peso visual (núcleo, cabos, porca, tubulação de força) saem
 * simplificadas, sem builder próprio de alto nível.
 */
import * as THREE from 'three';
import { aplicarUvDeMundo, aplicarIntemperiePorAltura } from '@/domain/components/cma/cmaAssetTextures';
import {
  box, cyl, cylAxis, insulator, pipeRun, roundedRect,
} from './geometriaHelpers';
import { HIGHLIGHT_TARGET } from './contrato';
import {
  buildFanUnit, FAN_SCALE, buildCurrentBolt, intemperieNaAltura,
} from './transformadorProcedural';

/*
 * Cotas do corpo — medidas do nó `Body` do `.glb` (bounding box, já na
 * escala de cena: `alturaAlvo: 9.5` / altura do `Body` no arquivo, ≈8,57×).
 * O eixo longo do CAD é Z; aqui já nasce em X (sem precisar do `giroY` que
 * `transformadorCad.js` aplica), então X/Z abaixo já estão trocados em
 * relação à medição bruta do arquivo.
 */
const TANK_W = 9.0;
const TANK_D = 4.4;
const TANK_H = 5.4;
const TANK_Y0 = 0; // `Body` já toca o chão no CAD — sem base separada
const LID_Y = TANK_Y0 + TANK_H;
const RAD_X = TANK_W / 2;

/*
 * Buchas de alta tensão (Bushing_B1/B2/B3 do CAD). A medição bruta das 3 não
 * saiu simetricamente espaçada (peculiaridade da malha de origem — ver §1
 * da spec, "aproximação aceita"); aqui elas nascem espaçadas de propósito,
 * como o Transformador 2 já faz, com a mesma altura total medida (topo dos
 * isoladores ≈ 9,4 m, perto do `alturaAlvo` de 9,5 do CAD).
 */
const HV_X = [-3.0, 0, 3.0];
const HV_BASE_Y = LID_Y - 0.35;
const HV_INSULATOR_LEN = 4.3;

/*
 * Buchas de baixa/média tensão (grupo `tensao` do CAD: Bushing_S1/S2/S3,
 * Bushing_M1, Bushing_mini1/2/3 — 7 no total, contra as 3 de
 * `transformadorProcedural.js`). `escala` é o fator sobre as cotas de
 * `buildLvBushingUnit`: 1 para S1-S3, um pouco maior para M1 (a maior do
 * grupo no CAD) e bem menor para as 3 "mini".
 */
const LV_BUSHINGS = [
  { x: -1.6, z: -1.2, escala: 1.0 }, // Bushing_S1
  { x: -0.3, z: -1.2, escala: 1.0 }, // Bushing_S2
  { x: 1.0, z: -1.2, escala: 1.0 }, // Bushing_S3
  { x: 2.6, z: -1.2, escala: 1.15 }, // Bushing_M1 — a maior do grupo
  { x: -2.6, z: 1.0, escala: 0.55 }, // Bushing_mini1
  { x: -0.6, z: 1.0, escala: 0.55 }, // Bushing_mini2
  { x: 1.4, z: 1.0, escala: 0.55 }, // Bushing_mini3
];

/*
 * Ventiladores de refrigeração (Cooling_Fan1/Cooling_Fan2). Diferente do
 * Transformador 2 (3 unidades em banco único, soprando para cima através de
 * painéis), o CAD tem só 2, um de cada lado do tanque — por isso aqui giram
 * 90° em X e sopram para os LADOS (Z), montados direto no flanco do tanque.
 */
const FAN_SIDE_X = 1.1;
const FAN_SIDE_Y = TANK_H * 0.55;
const FAN_SIDE_Z = TANK_D / 2 + 0.05;
const FAN_SIDE_RADIUS = 1.35;
const FAN_SPEEDS = [4.8, 5.2];

/* Monitor de gás online (Online_DGA) — ponta do tanque, meia altura. */
const DGA_X = RAD_X - 0.9;
const DGA_Y = TANK_H * 0.55;
const DGA_Z = TANK_D / 2 + 0.28;

/* Bolsa/termômetro de topo de óleo (Top_Oil_Temp) — ponta OPOSTA ao DGA, no
 * topo, mesma peça física que atende os cards `temperaturas` e `bolsa`,
 * herdando a decisão já tomada no CAD (transformadorCad.js, ANCHORS_CAD). */
const OIL_TEMP_X = -RAD_X + 1.0;
const OIL_TEMP_Y = LID_Y + 0.30;

/*
 * As 10 peças procedurais deste módulo — uma entrada por builder registrado
 * em `builders` (§3 do guia cma-transformador2-guia-construcao.md) — e a
 * direção de explosão de cada uma, herdada quase 1:1 de `PART_EXPLODE_CAD`
 * (transformadorCad.js), só agrupando as 22 peças nomeadas do `.glb` nestes
 * 10 ids de mais alto nível (ver tabela §3 da spec
 * cma-transformador1-para-procedural-potencia3.md).
 */
const PART_EXPLODE = {
  tanque: [0, 0, 0],
  buchasAT: [1, 2, 0],
  buchasBT: [-1, 2, 0],
  ventiladorEsquerdo: [-1, 0, -3],
  ventiladorDireito: [0, 0, 3],
  gas: [3, 0, 1],
  termometro: [-2, 3, -1],
  tubulacao: [-2, 0, -1],
  nucleo: [0, 1, 0],
  cabos: [0, -2, 0],
};

/*
 * Âncoras de card — mesmos ids de card do CAD (`ANCHORS_CAD`) e a mesma
 * convenção de reaproveitar a peça física em dois cards diferentes:
 * `temperaturas`/`bolsa` → a mesma bolsa de topo de óleo; `gas`/`eletricos`
 * → o mesmo monitor de gás online. `comutador` fica de fora de propósito —
 * não há peça equivalente no CAD (mesma decisão de transformadorCad.js).
 */
const ANCHOR_DEFS = [
  { id: 'buchas', part: 'buchasAT', point: [0, HV_BASE_Y + HV_INSULATOR_LEN - 0.4, 0] },
  { id: 'tensao', part: 'buchasBT', point: [0, LID_Y + 1.0, -1.2] },
  { id: 'gas', part: 'gas', point: [0, 0, 0] },
  { id: 'eletricos', part: 'gas', point: [0, 0, 0] },
  { id: 'temperaturas', part: 'termometro', point: [0, 0, 0] },
  { id: 'bolsa', part: 'termometro', point: [0, 0.3, 0] },
  { id: 'resfriamento', part: 'ventiladorDireito', point: [0, 0, 0.6] },
];

function buildTank(m) {
  const g = new THREE.Group();
  const tank = box(TANK_W, TANK_H, TANK_D, m.tanque, 0, TANK_Y0 + TANK_H / 2, 0);
  tank.name = HIGHLIGHT_TARGET;
  g.add(tank);

  // flanges do topo/base, mesma linguagem visual do Transformador 2
  g.add(box(TANK_W + 0.20, 0.14, TANK_D + 0.20, m.chapa, 0, TANK_Y0 + 0.07, 0));
  g.add(box(TANK_W + 0.22, 0.16, TANK_D + 0.22, m.flange, 0, LID_Y - 0.08, 0));

  // reforços verticais, um a cada ~1,2 m ao longo do tanque
  for (let i = -3; i <= 3; i += 1) {
    [-TANK_D / 2 - 0.08, TANK_D / 2 + 0.08].forEach((z) => {
      g.add(box(0.18, TANK_H - 0.30, 0.16, m.chapa, i * 1.2, TANK_Y0 + TANK_H / 2, z));
    });
  }
  return g;
}

/**
 * As 3 buchas de alta tensão (Bushing_B1/B2/B3). Corpo mais alto que o do
 * Transformador 2 (isolador de 4,3 m contra 3,3 m) — proporção medida no
 * CAD, onde o topo dos isoladores chega perto da altura total do ativo.
 */
function buildHvBushings(m) {
  const g = new THREE.Group();
  HV_X.forEach((x) => {
    const b = new THREE.Group();
    b.position.set(x, HV_BASE_Y, 0);
    b.add(cyl(0.62, 0.6, m.tanque, 0, 0.30, 0, 28));
    b.add(cyl(0.72, 0.14, m.flange, 0, 0.10, 0, 28));
    b.add(cyl(0.66, 0.12, m.flange, 0, 0.58, 0, 28));
    b.add(cyl(0.50, 0.28, m.terminal, 0, 0.72, 0, 24));
    const ins = insulator(HV_INSULATOR_LEN, 0.19, 0.34, 24, m.porcelana, 24);
    ins.position.y = 0.86;
    b.add(ins);
    b.add(cyl(0.26, 0.42, m.terminal, 0, HV_INSULATOR_LEN + 0.90, 0, 20));
    b.add(cyl(0.14, 0.36, m.terminal, 0, HV_INSULATOR_LEN + 1.28, 0, 16));
    b.add(cyl(0.06, 0.55, m.flange, 0, HV_INSULATOR_LEN + 1.68, 0, 12));
    g.add(b);
  });
  return g;
}

/**
 * Uma bucha de baixa/média tensão em RAIO 1 (`escala` decide o porte final)
 * — a unidade que `buildLvBushings` posiciona 7 vezes, com o mesmo raio de
 * eletricidade animada (`buildCurrentBolt`) que o Transformador 2 já usa
 * nas suas.
 */
function buildLvBushingUnit(m, escala) {
  const b = new THREE.Group();
  b.scale.setScalar(escala);
  b.add(cyl(0.44, 0.55, m.tanque, 0, 0.28, 0, 24));
  b.add(cyl(0.54, 0.12, m.flange, 0, 0.56, 0, 24));
  const ins = insulator(1.35, 0.15, 0.27, 11, m.porcelana, 20);
  ins.position.y = 0.64;
  b.add(ins);
  b.add(cyl(0.18, 0.28, m.terminal, 0, 2.12, 0, 18));
  b.add(cyl(0.05, 0.30, m.flange, 0, 2.40, 0, 10));
  b.add(buildCurrentBolt(m));
  return b;
}

/**
 * As 7 buchas do grupo `tensao` (Bushing_S1/S2/S3 + Bushing_M1 +
 * Bushing_mini1/2/3), contra as 3 do Transformador 2 — a diferença de
 * contagem em relação ao CAD (item §5.1 da spec).
 */
function buildLvBushings(m) {
  const g = new THREE.Group();
  LV_BUSHINGS.forEach(({ x, z, escala }) => {
    const unidade = buildLvBushingUnit(m, escala);
    unidade.position.set(x, LID_Y + 0.18, z);
    g.add(unidade);
  });
  return g;
}

/**
 * Um ventilador lateral, montado direto no flanco do tanque e girado 90° em
 * X para soprar em Z — diferente do banco de painéis do Transformador 2
 * (3 unidades soprando para cima), o CAD tem 2 unidades, uma de cada lado,
 * soprando para fora (item §5.2 da spec). Reaproveita `buildFanUnit` (raio 1)
 * e `FAN_SCALE`, exportados por transformadorProcedural.js.
 */
function buildSideFan(m, x, z, sentido, giro, fase) {
  const grupo = new THREE.Group();
  grupo.position.set(x, FAN_SIDE_Y, z);
  grupo.rotation.x = sentido * (Math.PI / 2);

  const ventilador = buildFanUnit(m, giro, fase);
  ventilador.scale.setScalar(FAN_SCALE * (FAN_SIDE_RADIUS / 0.46));
  grupo.add(ventilador);
  return grupo;
}

function buildCoolingFanLeft(m) {
  const g = new THREE.Group();
  g.add(buildSideFan(m, FAN_SIDE_X, -FAN_SIDE_Z, -1, FAN_SPEEDS[0], 0));
  return g;
}

function buildCoolingFanRight(m) {
  const g = new THREE.Group();
  g.add(buildSideFan(m, FAN_SIDE_X, FAN_SIDE_Z, 1, FAN_SPEEDS[1], 0.6));
  return g;
}

/**
 * Monitor de gás dissolvido online (Online_DGA) — mesma peça física que
 * atende os cards `gas` e `eletricos` no CAD (transformadorCad.js).
 */
function buildOnlineDga(m) {
  const g = new THREE.Group();
  const cabinet = box(0.62, 0.85, 0.30, m.prateado, DGA_X, DGA_Y, DGA_Z);
  cabinet.name = HIGHLIGHT_TARGET;
  g.add(cabinet);
  g.add(box(0.50, 0.60, 0.05, m.painelEscuro, DGA_X, DGA_Y + 0.05, DGA_Z + 0.16));
  g.add(roundedRect(0.36, 0.24, 0.03, 0.02, m.tela, DGA_X, DGA_Y + 0.15, DGA_Z + 0.19));
  g.add(cylAxis(0.10, 0.35, m.tubo, DGA_X, DGA_Y - 0.55, DGA_Z, 'z', 12));
  return g;
}

/**
 * Bolsa/termômetro de topo de óleo (Top_Oil_Temp) — a mesma peça física que
 * os cards `temperaturas` e `bolsa` referenciam, herdando a decisão já
 * tomada no CAD (ver §5.7/§8.2 da spec de origem).
 */
function buildOilBulb(m) {
  const g = new THREE.Group();
  const corpo = cyl(0.34, 0.55, m.tanque, OIL_TEMP_X, OIL_TEMP_Y, 0, 20);
  corpo.name = HIGHLIGHT_TARGET;
  g.add(corpo);
  g.add(cyl(0.40, 0.10, m.flange, OIL_TEMP_X, OIL_TEMP_Y + 0.30, 0, 20));
  g.add(cylAxis(0.16, 0.20, m.prateado, OIL_TEMP_X, OIL_TEMP_Y + 0.42, 0, 'z', 16));
  return g;
}

/**
 * Tubulação de força (Power_pipe_S/Power_pipe_M) e o cano de dreno
 * (pipe_B) — peças de detalhe, sem peso visual de card, cobertas com
 * `pipeRun` direto em vez de um builder por peça (item §5.4 da spec).
 */
function buildPiping(m) {
  const g = new THREE.Group();
  pipeRun(g, [
    [-RAD_X - 0.10, LID_Y - 0.40, -0.60],
    [-RAD_X - 0.55, LID_Y - 0.10, -0.60],
    [-RAD_X - 0.55, TANK_H * 0.45, -0.60],
  ], 0.09, m.tubo);
  g.add(cylAxis(0.08, 0.45, m.tubo, 0, TANK_Y0 - 0.05, TANK_D / 2 + 0.15, 'x', 12));
  return g;
}

/**
 * Núcleo (iron_S) — peça estrutural interna, sem forma própria visível na
 * malha real; representada como um volume simples dentro do tanque (item
 * §5.4 da spec).
 */
function buildCore(m) {
  const g = new THREE.Group();
  g.add(box(0.9, TANK_H * 0.6, TANK_D * 0.5, m.escuro, 0, TANK_H * 0.5, 0));
  return g;
}

/**
 * Cabos/condutores internos (Wire_M, Wire_B) e a porca de fixação (Nut) —
 * detalhes de baixo peso visual (item §5.4 da spec).
 */
function buildCablesAndNut(m) {
  const g = new THREE.Group();
  pipeRun(g, [
    [-0.6, TANK_H * 0.7, 0.4], [0, TANK_H * 0.6, 0.2], [0.6, TANK_H * 0.5, -0.3],
  ], 0.04, m.preto);
  g.add(cyl(0.10, 0.05, m.flange, 0, TANK_Y0 + 0.20, 0, 12));
  return g;
}

/**
 * Cria o modelo procedural com o LAYOUT DE PEÇAS do Transformador 1 (CAD),
 * para o slot `potencia-3` — mesma assinatura/contrato de
 * `criarModeloProcedural` (transformadorProcedural.js), ver
 * models/contrato.js.
 *
 * @param {Object} spec Entrada de TRANSFORMER_MODELS (só `spec.id` é lido).
 * @param {Object} contexto Ligações com a cena, injetadas pelo viewer.
 * @param {Object} contexto.materials Dicionário de materiais (criarMateriais).
 * @param {number} contexto.deslocamentoX Deslocamento do ativo no palco (ASSET_SHIFT_X).
 * @param {number} contexto.envMapIntensity Intensidade de ambiente do tema corrente.
 * @returns {Object} Modelo no formato de contrato.js.
 */
export function criarModeloProceduralCad(spec, contexto) {
  const { materials: m, deslocamentoX, envMapIntensity } = contexto;
  const group = new THREE.Group();
  const parts = {};

  const builders = {
    tanque: buildTank,
    buchasAT: buildHvBushings,
    buchasBT: buildLvBushings,
    ventiladorEsquerdo: buildCoolingFanLeft,
    ventiladorDireito: buildCoolingFanRight,
    gas: buildOnlineDga,
    termometro: buildOilBulb,
    tubulacao: buildPiping,
    nucleo: buildCore,
    cabos: buildCablesAndNut,
  };

  Object.keys(builders).forEach((id) => {
    const partGroup = builders[id](m);
    partGroup.name = id;
    partGroup.userData.home = new THREE.Vector3();
    partGroup.userData.explode = new THREE.Vector3().fromArray(PART_EXPLODE[id]);
    group.add(partGroup);
    parts[id] = partGroup;
  });

  group.position.x = deslocamentoX;
  group.updateMatrixWorld(true);

  group.traverse((object) => {
    if (object.isMesh) {
      object.castShadow = true;
      object.receiveShadow = true;
      if (object.geometry && !object.geometry.userData.uvDeMundo) {
        aplicarUvDeMundo(object.geometry);
        object.geometry.userData.uvDeMundo = true;
      }
      if (object.geometry) {
        const e = object.matrixWorld.elements;
        const cota = `${e[1]}|${e[5]}|${e[9]}|${e[13]}`;
        if (object.geometry.userData.intemperie !== cota) {
          if (object.geometry.userData.intemperie) {
            object.geometry = object.geometry.clone();
          }
          aplicarIntemperiePorAltura(object.geometry, e, intemperieNaAltura);
          object.geometry.userData.intemperie = cota;
        }
      }
    }
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    materials.forEach((material) => {
      if (!material) return;
      material.envMapIntensity = material.userData.envMap === undefined
        ? envMapIntensity
        : material.userData.envMap;
    });
  });

  const anchors = ANCHOR_DEFS.map(def => ({
    id: def.id,
    part: parts[def.part],
    point: new THREE.Vector3().fromArray(def.point),
  }));

  const helices = [];
  const ponteiros = [];
  const raios = [];
  group.traverse((object) => {
    if (object.userData.giro) helices.push(object);
    if (object.userData.repouso !== undefined) ponteiros.push(object);
    if (object.userData.percurso) raios.push(object);
  });

  return {
    id: spec.id, group, parts, anchors, helices, ponteiros, raios,
  };
}

export default criarModeloProceduralCad;
